import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  EMPLOYMENT_STATUSES,
  PREFERRED_TRACKS,
  employmentLabelKey,
  fromTriState,
  trackLabelKey,
  triState,
} from 'core/hooks';
import { useErpSubmitFundingFormMutation } from 'core/graphql';

/** Вопросы анкеты — ровно те, что хранит `FundingCandidate`. */
const TRI_QUESTIONS = [
  'isAdult',
  // Порог — 50 лет (решение владельца). В источнике было 55, и при переходе
  // пришлось обнулить 771 ответ; спрашивать надо ровно то, что означает поле.
  'is50Plus',
  'isUnemployed',
  'jdgSuspended',
  'hasDisabilityCertificate',
  'inFinancialHardship',
  'usedFundingBefore',
  'employerCanApply',
] as const;

const readUtm = (search: string) => {
  const params = new URLSearchParams(search);
  const utm: Record<string, string> = {};
  (['source', 'medium', 'campaign', 'content', 'term'] as const).forEach(
    (key) => {
      const value = params.get(`utm_${key}`);
      if (value) utm[key] = value;
    },
  );
  return utm;
};

/**
 * ТЗ 0024 — публичная форма заявки на со-финансирование.
 *
 * Единственный анонимный экран приложения. Он намеренно ничего не сообщает о
 * том, знаком ли адрес: ответ сервера одинаков для знакомого и незнакомого
 * e-mail, и экран не имеет права эту симметрию нарушать — ни текстом «вы уже
 * подавали», ни ссылкой на карточку.
 *
 * Анкета трёхпозиционная и здесь тоже: «не знаю» — законный ответ, а не
 * пропуск. Чекбокс превратил бы его в «нет» и отсёк бы человека от оператора,
 * который как раз таких и берёт.
 *
 * reCAPTCHA берётся из `window.grecaptcha`, если ключ задан. Токен уходит на
 * сервер, и решение принимает он — клиентская проверка ничего не гарантирует.
 */
export const FundingIntakeForm = memo(() => {
  const { t } = useTranslation();
  const [submit, { loading }] = useErpSubmitFundingFormMutation();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const utm = useMemo(() => readUtm(window.location.search), []);
  const set = (name: string, value: unknown) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const canSend =
    Boolean(String(values.email || '').trim()) ||
    Boolean(String(values.phone || '').trim());

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const token = await getRecaptchaToken();
      const { data } = await submit({
        variables: {
          payload: JSON.stringify(values),
          recaptchaToken: token,
          utm: JSON.stringify(utm),
        },
      });
      const result = data?.submitFundingForm;
      if (result?.success) {
        setDone(true);
        return;
      }
      // Сообщения сервера в этом домене русские, а форму читает поляк с
      // улицы. Переводим по ПОЛЮ ошибки, а сам текст сервера не печатаем.
      const failedField = result?.customErrors?.[0]?.field;
      setError(
        t(
          failedField === 'recaptchaToken'
            ? 'pages.fundingForm.errorCaptcha'
            : failedField === 'payload'
            ? 'pages.fundingForm.errorRateLimited'
            : 'pages.fundingForm.errorGeneric',
        ),
      );
    } catch (requestError) {
      setError(t('pages.fundingForm.errorGeneric'));
    }
  };

  if (done) {
    return (
      <Page>
        <Card>
          <Title>{t('pages.fundingForm.thanksTitle')}</Title>
          <p>{t('pages.fundingForm.thanksBody')}</p>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Card as="form" onSubmit={send}>
        <Title>{t('pages.fundingForm.title')}</Title>
        <p>{t('pages.fundingForm.intro')}</p>

        {(['fullName', 'email', 'phone', 'city'] as const).map((name) => (
          <Field key={name}>
            <Label htmlFor={`dofin-${name}`}>
              {t(`pages.dofinansowanie.fields.${name}`)}
            </Label>
            <Input
              id={`dofin-${name}`}
              value={String(values[name] || '')}
              onChange={(event) => set(name, event.target.value)}
            />
          </Field>
        ))}

        <Field>
          <Label htmlFor="dofin-employment">
            {t('pages.dofinansowanie.fields.employmentStatus')}
          </Label>
          <Select
            id="dofin-employment"
            value={String(values.employmentStatus || '')}
            onChange={(event) => set('employmentStatus', event.target.value)}
          >
            <option value="">{t('pages.fundingForm.triUnknown')}</option>
            {EMPLOYMENT_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(employmentLabelKey(value))}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label htmlFor="dofin-employer">
            {t('pages.dofinansowanie.fields.employerName')}
          </Label>
          <Input
            id="dofin-employer"
            value={String(values.employerName || '')}
            onChange={(event) => set('employerName', event.target.value)}
          />
        </Field>

        {TRI_QUESTIONS.map((name) => (
          <Field key={name}>
            <Label htmlFor={`dofin-${name}`}>
              {t(`pages.dofinansowanie.fields.${name}`)}
            </Label>
            <Select
              id={`dofin-${name}`}
              value={triState(values[name] as boolean | null)}
              onChange={(event) =>
                set(name, fromTriState(event.target.value as never))
              }
            >
              {/* «Не спрашивали» — взгляд МЕНЕДЖЕРА на то же значение;
                  человеку с улицы это ничего не говорит, он отвечает сам.
                  Значение на проводе то же, слова разные. */}
              {(['unknown', 'yes', 'no'] as const).map((value) => (
                <option key={value} value={value}>
                  {value === 'unknown'
                    ? t('pages.fundingForm.triUnknown')
                    : t(`pages.dofinansowanie.tri.${value}`)}
                </option>
              ))}
            </Select>
          </Field>
        ))}

        <Field>
          <Label htmlFor="dofin-tracks">
            {t('pages.dofinansowanie.fields.preferredTracks')}
          </Label>
          <Select
            id="dofin-tracks"
            value={String((values.preferredTracks as string[])?.[0] || '')}
            onChange={(event) =>
              set(
                'preferredTracks',
                event.target.value ? [event.target.value] : [],
              )
            }
          >
            <option value="">—</option>
            {PREFERRED_TRACKS.map((value) => (
              <option key={value} value={value}>
                {t(trackLabelKey(value))}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label htmlFor="dofin-notes">
            {t('pages.fundingForm.commentLabel')}
          </Label>
          <Textarea
            id="dofin-notes"
            value={String(values.notes || '')}
            onChange={(event) => set('notes', event.target.value)}
          />
        </Field>

        {error && <ErrorBox>{error}</ErrorBox>}

        <Submit type="submit" disabled={!canSend || loading}>
          {t('pages.fundingForm.submit')}
        </Submit>
        {!canSend && <Hint>{t('pages.fundingForm.needContact')}</Hint>}
        <Hint>{t('pages.fundingForm.privacy')}</Hint>
      </Card>
    </Page>
  );
});

FundingIntakeForm.displayName = 'FundingIntakeForm';

/**
 * Токен reCAPTCHA, если скрипт подключён и ключ задан. Пустая строка —
 * законный исход: решение всё равно принимает сервер, а падать на глазах у
 * заявителя из-за отсутствующего скрипта нельзя.
 */
const getRecaptchaToken = async (): Promise<string> => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const { grecaptcha } = (window as unknown) as { grecaptcha?: any };
  if (!siteKey || !grecaptcha?.execute) return '';
  try {
    await new Promise<void>((resolve) => grecaptcha.ready(resolve));
    return await grecaptcha.execute(siteKey, { action: 'dofin_form' });
  } catch {
    return '';
  }
};

const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding: 32px 16px;
  display: flex;
  justify-content: center;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px 32px;
  width: 100%;
  max-width: 620px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
`;

const Field = styled.div`
  margin: 14px 0;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  background: #fff;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 90px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
`;

const Submit = styled.button`
  margin-top: 18px;
  width: 100%;
  border: 0;
  border-radius: 8px;
  background: #2563eb;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  padding: 11px 0;
  cursor: pointer;

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`;

const Hint = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: #94a3b8;
`;

const ErrorBox = styled.div`
  margin-top: 14px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
`;
